import { reorderChildren, resolveGridWith, resolvedBreakpoint } from '@/helper/Resolver';
import { isValidElement, useMemo, Children, cloneElement } from 'react';

export const useResponsiveGrid = (props) => {
    
    const {
        xs = null,
        sm = null, 
        md = null,
        lg = null,
        xl = null,

        style = {},
        children = null,
        window = null,

        ...rest
    } = props;

    const windowWidth = window?.width > 0 ? Math.ceil(window.width - 5) : 1;

    const childrenArray = useMemo(() => 
        Children.toArray(children)
    , [children]);

    const itemsPerRow = useMemo(() => {
        const gridValues = childrenArray.map(child =>
            resolveGridWith(child.props, windowWidth)
        );

        const values = gridValues.map(v => Math.min(12, v));

        let sum = 0;
        let count = 0;

        for (const v of values) {
            if (!v || v < 0) 
                continue;
            
            if (sum + v > 12) 
                break;

            sum += v;
            count += 1;

            if (sum === 12) break;
        }

        return count;
    }, [childrenArray, windowWidth]);

    const gapCount = useMemo(() => {
        if (itemsPerRow <= 1) return 0;
        return itemsPerRow - 1;
    }, [itemsPerRow]);

    const gapSource = rest.gap ?? style.gap;

    const gap = useMemo(() => {
        
        if (gapSource && typeof gapSource === "object")
            return resolvedBreakpoint(gapSource, windowWidth);
        
        return gapSource ?? false;
    }, [gapSource, windowWidth]);
    
    const possibleWidth = useMemo(() => {
        if (gap === false || gapCount === 0) 
            return windowWidth;

        return windowWidth - (gap * gapCount);
    }, [gap, gapCount, windowWidth]);

    const windowRatio = useMemo(() => {
        return possibleWidth / windowWidth;
    }, [possibleWidth, windowWidth]);

    const ordered = useMemo(() => {
        return reorderChildren(childrenArray, windowWidth);
    }, [childrenArray, windowWidth]);
    
    const orderedChildren = useMemo(() => {
        return ordered.map((child, index) => {
            if (!isValidElement(child)) return child;

            const flexValue = resolveGridWith(child.props, windowWidth);

            const percent = flexValue === 12
                ? 100
                : (flexValue / 12) * 100 * windowRatio;

            const extraStyle = {
                width: `${percent}%`,
                flexGrow: 0,
                flexShrink: 0,
            };

            return cloneElement(child, {
                // key stabil lassen (niemals von width abhängig machen)
                key: child.key ?? `row_col_${percent}_${possibleWidth}`,
                style: child.props?.style
                    ? [child.props.style, extraStyle]
                    : extraStyle,
            });
        })?.filter(child => child != null);
    }, [ordered, windowWidth, windowRatio]);

    return {
        orderedChildren
    }

};